import type { ReactNode } from 'react';
import styled from 'styled-components';
import { Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { AGE_GROUPS, type AgeGroup } from '../../../constants/ageGroup';
import { useMetaData } from '../../../hooks/data/useMetaData';
import { useCrossAggregationComparisonsData } from '../../../hooks/data/useCrossAggregationComparisonsData';
import { includes } from '../../../utils/array';
import {
  GroupedBarChart,
  type GroupedDataItem,
} from '../Chart/GroupedBarChart';
import { PieChart, type PieDataItem } from '../Chart/PieChart';
import { SchemaSelector } from './SchemaSelector';

type Props = {
  title: string;
  noDataMessage?: string;
};

type ChartData = {
  groupedData: GroupedDataItem[];
  flatData: PieDataItem[];
};

type GroupedDataSubItem = GroupedDataItem['data'][number];

const CrossAnalysisComparison = ({
  title,
  noDataMessage,
}: Props): ReactNode => {
  const { t } = useTranslation();
  const { allDataMetas } = useMetaData();
  const { isLoading, aggregationsMap } = useCrossAggregationComparisonsData();

  const data = Object.fromEntries(
    Object.entries(aggregationsMap).map(
      ([simDataId, aggregations]: [string, Map<string, Map<string, number>>]): [
        string,
        ChartData,
      ] => {
        const dataMeta = allDataMetas.get(simDataId);

        const groupedData = Array.from(aggregations)
          .filter(
            (
              item: [string, Map<string, number>]
            ): item is [AgeGroup, Map<string, number>] => {
              return includes(AGE_GROUPS, item[0]);
            }
          )
          .map(
            ([label, subItems]: [
              AgeGroup,
              Map<string, number>,
            ]): GroupedDataItem => {
              return {
                label:
                  dataMeta?.type === 'gad_standard' && label === '60'
                    ? t(`ageGroups.60s+`)
                    : t(`ageGroups.${label}s`),
                data: Array.from(subItems)
                  .map(
                    ([subLabel, value]: [
                      string,
                      number,
                    ]): GroupedDataSubItem => ({
                      subLabel:
                        subLabel === '1'
                          ? t('genders.male')
                          : t('genders.female'),
                      value: value,
                      color: subLabel === '1' ? '#023894' : '#db0228',
                    })
                  )
                  .reverse(),
              };
            }
          )
          .reverse();

        const flatData = groupedData.flatMap(
          (item: GroupedDataItem): PieDataItem[] => {
            return item.data.map(
              (subItem: GroupedDataSubItem): PieDataItem => ({
                label: `${item.label} × ${subItem.subLabel}`,
                value: subItem.value,
              })
            );
          }
        );

        return [simDataId, { groupedData: groupedData, flatData: flatData }];
      }
    )
  );

  return (
    <>
      <SchemaSelector />
      <ChartTitle>{title}</ChartTitle>
      <Spin spinning={isLoading}>
        <SchemaListContainer>
          {Object.entries(data).map(
            ([simDataId, { groupedData, flatData }]: [
              string,
              ChartData,
            ]): ReactNode => {
              const dataMeta = allDataMetas.get(simDataId);

              return (
                <SchemaContainer key={simDataId}>
                  {groupedData.length === 0 ? (
                    <NoDataContainer>
                      <Typography.Text type="secondary">
                        {noDataMessage}
                      </Typography.Text>
                    </NoDataContainer>
                  ) : (
                    <ContentContainer>
                      <GroupedBarChart
                        groupedData={groupedData}
                        unit={t('unit.agent')}
                      />
                      <PieChart data={flatData} unit={t('unit.agent')} />
                    </ContentContainer>
                  )}
                  <GraphTitle>{dataMeta?.name ?? ''}</GraphTitle>
                </SchemaContainer>
              );
            }
          )}
        </SchemaListContainer>
      </Spin>
    </>
  );
};

const ChartTitle = styled(Typography.Text)`
  display: block;
  font-weight: 600;
  font-size: 16px;
  color: #262626;
  margin: 16px 0 30px;
  text-align: center;
`;

const SchemaListContainer = styled.div`
  margin: 0 16px;
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const SchemaContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ContentContainer = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr 1fr;
  align-items: center;
`;

const NoDataContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
`;

const GraphTitle = styled.div`
  font-size: 12px;
  text-align: center;
`;

export { CrossAnalysisComparison };
